import type { OrderLeg } from '../services/orders.service';

// #33649 — auto-demote the order's existing DELIVERY stop when the user types a
// NEW one.
//
// An order may have exactly one DELIVERY stop (validateLegChanges). Before this,
// a re-delivery meant hand-clearing the old stop first — and that is precisely
// what users could not do once the original delivery leg had been paid out:
// canEditLegAction2 / canEditLegtype lock BOTH sides of a paid boundary, so the
// add-a-stop-and-retype-it flow dead-ended on "Only one DELIVERY is allowed per
// order (found 2)" with no cell left to fix it in. PRO 5029262 (driver marked
// arrived, leg paid out, container never actually delivered) is the case that
// drove this.
//
// So: typing any stop to DELIVERY demotes the previous DELIVERY stop to STOPOFF,
// deliberately writing THROUGH the paid-leg lock — that lock is the thing being
// worked around, not an invariant to respect here. The demotion is one-way; there
// is no revert. A user who demotes the wrong stop closes the pro without saving.
//
// Scoped to DELIVERY on purpose: PICKUP and TERMINATE carry the same
// one-per-order rule but not the same time pressure, and Admin Override (#33501)
// already covers them.
//
// STOPS, NOT LEGS. A move type lives at a BOUNDARY between two legs: leg N's
// destination (`legAction2`) IS leg N+1's origin (`legtype`). An N-leg chain has
// N+1 stops — leg 1's legtype, then every leg's legAction2 — so both sides of the
// demoted boundary have to be written or the chain contradicts itself. Stop
// indexes below follow that same sequence: stop 0 = legs[0].legtype, stop k =
// legs[k-1].legAction2.

export const DELIVERY = 'DELIVERY';
export const DEMOTED_TO = 'STOPOFF';

export type LegMoveTypeField = 'legtype' | 'legAction2';

export type DeliveryDemotionPlan = {
  /** Field writes that retype the old boundary — both sides — to STOPOFF. */
  writes: { legrecno: number; field: LegMoveTypeField; value: typeof DEMOTED_TO }[];
  /**
   * Display legnums to name in the user-facing notice, using the same
   * "the delivery leg is the leg whose legAction2 is DELIVERY" convention as the
   * `deliveryleg` pointer (legSavePayload). A DELIVERY sitting on stop 0 has no
   * such leg — it is not a saveable shape anyway (PICKUP must precede DELIVERY) —
   * and is reported as leg 1 so it still gets demoted and still gets named.
   */
  demotedLegnums: number[];
};

const moveTypeAt = (legs: OrderLeg[], stop: number): string =>
  String((stop === 0 ? legs[0]?.legtype : legs[stop - 1]?.legAction2) ?? '')
    .trim()
    .toUpperCase();

/**
 * Plan the demotion triggered by typing `field` on leg `legrecno` to `value`.
 *
 * `legs` must be the EFFECTIVE legs — composed and with the user's staged edits
 * already overlaid — in display order, because the stop being replaced may itself
 * be one the user retyped a moment ago. Returns null when nothing should happen:
 * the edit isn't a DELIVERY, the leg isn't in the list, or no OTHER stop holds
 * DELIVERY (including the no-op case of re-selecting DELIVERY on the stop that
 * already has it).
 *
 * Every other DELIVERY stop is demoted, not just the first. Two of them is
 * already an unsaveable state, and leaving one behind would hand the user the
 * same dead end this exists to clear.
 */
export function planDeliveryDemotion(args: {
  legs: OrderLeg[];
  legrecno: number;
  field: LegMoveTypeField;
  value: unknown;
}): DeliveryDemotionPlan | null {
  const { legs, legrecno, field } = args;
  if (String(args.value ?? '').trim().toUpperCase() !== DELIVERY) return null;

  const idx = legs.findIndex((l) => l.legrecno === legrecno);
  if (idx < 0) return null;

  // The stop the user just typed — the one boundary we must leave alone.
  // legAction2 on leg N is stop N+1; legtype on leg N is stop N.
  const typedStop = field === 'legAction2' ? idx + 1 : idx;

  const writes: DeliveryDemotionPlan['writes'] = [];
  const demotedLegnums: number[] = [];
  for (let stop = 0; stop <= legs.length; stop++) {
    if (stop === typedStop) continue;
    if (moveTypeAt(legs, stop) !== DELIVERY) continue;
    // Write both sides of the boundary: the leg before it owns the destination,
    // the leg after it owns the origin. Either may be absent at the chain's ends.
    const before = stop > 0 ? legs[stop - 1] : null;
    const after = stop < legs.length ? legs[stop] : null;
    if (before) writes.push({ legrecno: before.legrecno, field: 'legAction2', value: DEMOTED_TO });
    if (after) writes.push({ legrecno: after.legrecno, field: 'legtype', value: DEMOTED_TO });
    demotedLegnums.push((before ?? after)?.legnum ?? stop);
  }

  return writes.length ? { writes, demotedLegnums } : null;
}
